import { defineBoot } from '#q-app/wrappers';

import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { h } from 'vue';
import { Notify } from 'quasar';
import { useOidcStore } from 'src/stores/oidc';
import { useProfileStore } from 'src/stores/profile';
import * as Sentry from '@sentry/vue';
import {
  CLIENT_ID,
  OIDC_REDIRECT_URL,
  OIDC_REDIRECT_PATH,
  GUEST_CLIENT_ID,
  OIDC_AUTHORITY,
} from 'src/utils/config';
// Log.setLogger(console);
// Log.setLevel(Log.DEBUG);

import 'vue-router';

declare module 'vue-router' {
  interface RouteMeta {
    // is optional
    authz?: (params: { roles: { [key: string]: boolean } }) => boolean;
    public?: boolean;
  }
}

const resolveRedirectUri = () => {
  if (OIDC_REDIRECT_URL) {
    return OIDC_REDIRECT_URL;
  }

  if (OIDC_REDIRECT_PATH) {
    if (window.location.href.includes('guestaccess')) {
      return (
        window.location.protocol +
        '//' +
        window.location.host +
        OIDC_REDIRECT_PATH +
        '?guestaccess'
      );
    }
    return (
      window.location.protocol +
      '//' +
      window.location.host +
      OIDC_REDIRECT_PATH
    );
  }
};

const redirect_uri = resolveRedirectUri();

export const userManager = new UserManager({
  stateStore: new WebStorageStateStore(),
  authority: OIDC_AUTHORITY,
  client_id: window.location.href.includes('guestaccess')
    ? GUEST_CLIENT_ID
    : CLIENT_ID,
  redirect_uri,
  silent_redirect_uri: window.location.origin + '/oidc-silent-renew',
  scope: 'profile openid roles email',
  automaticSilentRenew: true,
});

const oidcContinue = useSessionStorage('oidc:continue', '');

function storeOidcContinueTarget(target: string) {
  if (!target || target.startsWith('/oidc-callback')) {
    return;
  }

  oidcContinue.value = target;
}

function resolveOidcContinueTarget() {
  if (oidcContinue.value) {
    const dest = oidcContinue.value;
    oidcContinue.value = '';
    return dest;
  }

  return '/';
}

let sentryUser = false;
function saveSentryUserWithToken(profile: any) {
  if (sentryUser) return;
  const user = {
    id: profile.preferred_username,
    username: profile.preferred_username,
    email: profile.email,
    impersonator_id: profile.impersonator?.id,
  };
  Sentry.setUser(user);
  sentryUser = true;
}

const createOidcRouteGuard = (
  userManager: UserManager,
  oidcStore: ReturnType<typeof useOidcStore>,
  profileStore: ReturnType<typeof useProfileStore>,
) => {
  oidcStore.setUserManager(userManager);

  return async (to: any, from: any, next: any) => {
    if (to.query.bearer) {
      const { token } = useAccessToken();
      token.value = to.query.bearer as string;
      next();
      return;
    }

    if (to.path === '/oidc-callback') {
      console.log('[oidc-callback] enter', {
        url: window.location.href,
        hasCode: !!to.query.code,
        hasState: !!to.query.state,
        hasError: !!to.query.error,
        error: to.query.error,
        oidcContinue: oidcContinue.value,
      });
      let callbackUser: Awaited<
        ReturnType<typeof oidcStore.signinRedirectCallback>
      >;
      try {
        callbackUser = await oidcStore.signinRedirectCallback();
        console.log('[oidc-callback] signinRedirectCallback resolved', {
          hasUser: !!callbackUser,
          profile: callbackUser?.profile?.preferred_username ?? null,
          expiresAt: callbackUser?.expires_at ?? null,
          audience: (callbackUser?.profile as any)?.aud ?? null,
          error: oidcStore.error?.message ?? null,
        });
      } catch (err) {
        console.error('[oidc-callback] signinRedirectCallback threw', err);
      }
      if (oidcStore.error) {
        console.error(
          '[oidc-callback] oidcStore.error after callback',
          oidcStore.error,
        );
      }
      profileStore.fetch();
      const dest = resolveOidcContinueTarget();
      console.log('[oidc-callback] redirecting to continue target', dest);
      next(dest);
    } else if (to.path === '/oidc-silent-renew') {
      await oidcStore.signinSilentCallback();
      next('/');
    } else {
      const disableAuthz = import.meta.env.VITE_MSW === 'true';

      // skip auth
      if (disableAuthz) {
        next();
        return;
      }

      const user = await userManager.getUser();
      const authenticated = user !== null;
      console.log('[oidc] isAuthenticated', authenticated);

      if (authenticated) {
        if (user?.expired) {
          try {
            await userManager.signinSilent();
          } catch (e) {
            storeOidcContinueTarget(to.fullPath);
            await userManager.signinRedirect();
          }
        }

        user && saveSentryUserWithToken(user.profile);
        await profileStore.resolve();

        // Check authz for all parent routes
        const matchedRoutes = to.matched;
        for (const route of matchedRoutes) {
          if (route.meta && route.meta.authz) {
            const { roles, effectiveRoles } = profileStore;
            console.log(
              '[authz] checking parent authz',
              route,
              route.meta.authz({ roles, effectiveRoles }),
            );
            if (!route.meta.authz({ roles, effectiveRoles })) {
              Notify.create({
                message: 'You are not authorized to access this page',
                color: 'negative',
              });
              next('/');
              return;
            }
          }
        }

        next();

        // if (to.meta.authz) {
        //   const { roles } = profileStore;
        //   console.log('[authz] checking authz', to, to.meta.authz({ roles }));
        //   if (to.meta.authz({ roles })) {
        //     next();
        //   } else {
        //     Notify.create({
        //       message: 'You are not authorized to access this page',
        //       color: 'negative',
        //     });
        //     next('/');
        //   }
        // } else {
        //   next();
        // }
      } else if (to.meta.public) {
        next();
      } else {
        storeOidcContinueTarget(to.fullPath);
        console.log(
          '[oidc] unauthenticated, storing continue and redirecting to Keycloak',
          {
            continueTo: to.fullPath,
          },
        );
        await oidcStore.signinRedirect();
        next();
      }
    }
  };
};

export default defineBoot(async ({ router }) => {
  const oidcStore = useOidcStore();
  const profileStore = useProfileStore();

  router.addRoute({
    path: '/oidc-callback',
    name: 'oidc-callback',
    component: { render: () => h('div', 'Loading...') },
  });

  router.beforeEach(createOidcRouteGuard(userManager, oidcStore, profileStore));
});
